import { useCallback, useEffect, useState } from "react";
import {
    AlertTriangle,
    ExternalLink,
    Loader2,
    RefreshCw,
    Trash2,
    Users,
} from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import {
    getDuplicatePeople,
    deleteDuplicatePerson,
    pappersUrl,
    DuplicatePeopleResponse,
    FlaggedEstablishment,
    DuplicatePerson,
} from "../../../interfaces/duplicatePeople";

/**
 * Review queue for establishments holding the same person twice (`/bo/duplicate-people`).
 *
 * Every row is a judgement call that no rule can make: two records, one name, two different
 * birthdates. Either they are two people who happen to share a name, or one person the imports
 * split in half. So the page's job is to put the evidence where the eye already is — what each
 * record carries, how many establishments hang off it, and a link straight to Pappers to check
 * the company against the registry — and then get out of the way.
 *
 * Deleting is one person at a time, behind a confirm. There is no bulk action on purpose: the
 * whole reason this queue exists is that the decision cannot be taken in bulk.
 */

const fmt = (n?: number) => (n === undefined || n === null ? "—" : n.toLocaleString("fr-FR"));

/** "03-1974" → "03/1974"; a missing date is the point of the row, so it is stated, not blanked. */
function birthdateLabel(value: string | null): string {
    if (!value) return "inconnue";
    return value.replace("-", "/");
}

export default function DuplicatePeople() {
    const [data, setData] = useState<DuplicatePeopleResponse | null>(null);
    const [rows, setRows] = useState<FlaggedEstablishment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [page, setPage] = useState(1);
    const [confidentOnly, setConfidentOnly] = useState(true);
    const [matchOnKey, setMatchOnKey] = useState(false);

    const [confirming, setConfirming] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const load = useCallback(
        async (opts: { refresh?: boolean } = {}) => {
            setLoading(true);
            setError(null);
            try {
                const res = await getDuplicatePeople({
                    page,
                    limit: 25,
                    confidentOnly,
                    matchOnKey,
                    refresh: opts.refresh,
                });
                setData(res);
                setRows(res.results);
            } catch (e: any) {
                setError(e?.response?.data?.error ?? e?.message ?? "Chargement impossible.");
            } finally {
                setLoading(false);
            }
        },
        [page, confidentOnly, matchOnKey]
    );

    useEffect(() => {
        load();
    }, [load]);

    /**
     * Removes the person from the list in place rather than refetching: the scan behind this page
     * takes a full pass over the establishments, and reloading it after every delete would make
     * working through a queue unbearable. A group that drops to one person, and an establishment
     * left with no group, disappear the same way they would on a fresh scan.
     */
    const onDelete = async (person: DuplicatePerson) => {
        setDeleting(person._id);
        try {
            const res = await deleteDuplicatePerson(person._id);
            setNotice(
                `« ${res.name} » supprimée — détachée de ${res.unlinkedFromEstablishments} établissement(s) ` +
                `et ${res.unlinkedFromEvents} évènement(s).`
            );
            setRows(prev =>
                prev
                    .map(est => ({
                        ...est,
                        groups: est.groups
                            .map(g => ({ ...g, people: g.people.filter(p => p._id !== person._id) }))
                            .filter(g => g.people.length >= 2),
                    }))
                    .filter(est => est.groups.length > 0)
            );
        } catch (e: any) {
            setError(e?.response?.data?.error ?? e?.message ?? "Suppression impossible.");
        } finally {
            setDeleting(null);
            setConfirming(null);
        }
    };

    const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

    return (
        <div className="min-h-screen bg-[#faf9f7]">
            <BONavigation />

            <div className="mx-auto max-w-6xl px-6 py-6">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                            <Users className="h-5 w-5 text-gray-400" />
                            Doublons de dirigeants
                        </h1>
                        <p className="mt-1 text-sm text-gray-500 max-w-2xl">
                            Établissements portant deux fiches au même nom avec des dates de naissance
                            différentes. Soit deux personnes homonymes — rien à faire — soit une seule
                            personne coupée en deux par les imports. Vérifiez sur Pappers, puis supprimez
                            la fiche en trop.
                        </p>
                    </div>
                    <button
                        onClick={() => load({ refresh: true })}
                        disabled={loading}
                        className="shrink-0 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <RefreshCw className="h-4 w-4" />
                        )}
                        Recalculer
                    </button>
                </div>

                {/* ── Counters ────────────────────────────────────────────── */}
                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-xl overflow-hidden">
                    {[
                        { label: "Établissements signalés", value: fmt(data?.total) },
                        { label: "Établissements parcourus", value: fmt(data?.scanned) },
                        {
                            label: "Calculé à",
                            value: data ? new Date(data.computedAt).toLocaleTimeString("fr-FR") : "—",
                        },
                        {
                            label: "Page",
                            value: data ? `${data.page} / ${totalPages}` : "—",
                        },
                    ].map(c => (
                        <div key={c.label} className="bg-white px-4 py-3">
                            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                                {c.label}
                            </div>
                            <div className="text-lg font-semibold tabular-nums text-gray-900">{c.value}</div>
                        </div>
                    ))}
                </div>

                {/* ── Filters ─────────────────────────────────────────────── */}
                <div className="mt-4 flex flex-wrap items-center gap-5 rounded-xl border border-gray-200 bg-white px-4 py-3">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={confidentOnly}
                            onChange={e => {
                                setPage(1);
                                setConfidentOnly(e.target.checked);
                            }}
                            className="h-4 w-4 rounded border-gray-300"
                        />
                        Deux dates connues et différentes
                        <span className="text-gray-400">
                            — décoché, affiche aussi « une datée, une sans date »
                        </span>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={matchOnKey}
                            onChange={e => {
                                setPage(1);
                                setMatchOnKey(e.target.checked);
                            }}
                            className="h-4 w-4 rounded border-gray-300"
                        />
                        Ignorer accents et casse
                        <span className="text-gray-400">— « Jerome » = « Jérôme »</span>
                    </label>
                </div>

                {notice && (
                    <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                        {notice}
                    </div>
                )}
                {error && (
                    <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        {error}
                    </div>
                )}
                {data?.truncated && (
                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        Liste tronquée : le maximum de résultats a été atteint. Traitez ceux-ci puis
                        relancez le calcul.
                    </div>
                )}

                {/* ── Results ─────────────────────────────────────────────── */}
                {loading && rows.length === 0 && (
                    <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Parcours des établissements…
                    </div>
                )}

                {!loading && rows.length === 0 && (
                    <div className="mt-8 rounded-xl border border-gray-200 bg-white px-6 py-10 text-center">
                        <div className="text-sm font-medium text-gray-900">Aucun doublon à traiter.</div>
                        <div className="mt-1 text-sm text-gray-500">
                            Aucun établissement ne porte deux fiches au même nom avec des dates différentes.
                        </div>
                    </div>
                )}

                <div className="mt-4 space-y-3">
                    {rows.map(est => {
                        const pappers = pappersUrl(est.siren);
                        return (
                            <div
                                key={est._id}
                                className="rounded-xl border border-gray-200 bg-white overflow-hidden"
                            >
                                <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-4 py-3">
                                    <div className="min-w-0">
                                        <div className="truncate text-sm font-semibold text-gray-900">
                                            {est.name || "(sans nom)"}
                                        </div>
                                        <div className="mt-0.5 text-xs text-gray-500 tabular-nums">
                                            {[est.city, est.siren && `SIREN ${est.siren}`, est.siret && `SIRET ${est.siret}`]
                                                .filter(Boolean)
                                                .join(" · ")}
                                        </div>
                                    </div>
                                    {pappers && (
                                        <a
                                            href={pappers}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                        >
                                            Pappers
                                            <ExternalLink className="h-3.5 w-3.5" />
                                        </a>
                                    )}
                                </div>

                                {est.groups.map(group => (
                                    <div key={group.name} className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-900">{group.name}</span>
                                            <span
                                                className={
                                                    "rounded-full px-2 py-0.5 text-[10px] font-medium " +
                                                    (group.confident
                                                        ? "bg-red-50 text-red-700"
                                                        : "bg-amber-50 text-amber-700")
                                                }
                                            >
                                                {group.confident ? "dates différentes" : "une date manquante"}
                                            </span>
                                        </div>

                                        <div className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-100">
                                            {group.people.map(p => (
                                                <div
                                                    key={p._id}
                                                    className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5"
                                                >
                                                    <div className="min-w-0 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                                                        <span className="tabular-nums font-medium text-gray-900">
                                                            {birthdateLabel(p.birthdate)}
                                                        </span>
                                                        {p.qualite && (
                                                            <span className="text-gray-500">{p.qualite}</span>
                                                        )}
                                                        <span className="text-gray-500 tabular-nums">
                                                            {p.linkedEstablishments} établissement
                                                            {p.linkedEstablishments > 1 ? "s" : ""}
                                                        </span>
                                                        <span className="text-gray-500 tabular-nums">
                                                            {p.enrichmentsWithData} donnée
                                                            {p.enrichmentsWithData > 1 ? "s" : ""}
                                                        </span>
                                                        {p.hasLinkedin && (
                                                            <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">
                                                                LinkedIn
                                                            </span>
                                                        )}
                                                    </div>

                                                    {confirming === p._id ? (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-gray-500">
                                                                Supprimer définitivement ?
                                                            </span>
                                                            <button
                                                                onClick={() => onDelete(p)}
                                                                disabled={deleting === p._id}
                                                                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                                                            >
                                                                {deleting === p._id && (
                                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                                )}
                                                                Confirmer
                                                            </button>
                                                            <button
                                                                onClick={() => setConfirming(null)}
                                                                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                                            >
                                                                Annuler
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => setConfirming(p._id)}
                                                            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700"
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                            Supprimer
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>

                {/* ── Pagination ──────────────────────────────────────────── */}
                {data && data.total > data.limit && (
                    <div className="mt-5 flex items-center justify-center gap-3">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page <= 1 || loading}
                            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                        >
                            Précédent
                        </button>
                        <span className="text-sm text-gray-500 tabular-nums">
                            {page} / {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages || loading}
                            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                        >
                            Suivant
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
